"use strict";

import angular from "angular";

import template from "./indicator-values.component.html";
import "./themes/indicator-values.component-dark.scss";
import "./themes/indicator-values.component.scss";

import _ from "lodash";
import { Available } from "../../../gateway/Available/Available";
import { CalculatingMachineV2Gateway } from "../../../gateway/CalculatingMachineV2Gateway/CalculatingMachineV2Gateway";
import { Point } from "../../../gateway/Point/Point";
import { PointCalculated } from "../../../gateway/PointCalculated/PointCalculated";
import { Serie } from "../../../gateway/Serie/Serie";
import { CalculatingMachine } from "../../../presentation/services/CalculatingMachine/CalculatingMachine";
import { FormatUnits } from "../../../presentation/services/FormatUnits/FormatUnits";
import {
    accumulationWindow,
    dataValuesMode,
    lastMonthWithData,
    mergeMonthlyComparations,
    monthsBetween,
    rangeLabel,
    valueTooltip
} from "../../../utils/year-to-date-comparation";

/**
 * Buscas de meses do acumulado EM ANDAMENTO, por empresa/fonte/cenario/ano/series. Varios cards do
 * mesmo dashboard pedem o mesmo ano ao mesmo tempo; sem isto, cada um dispararia o seu calculo.
 */
const buscasDoAcumulado = new Map();
/**
 * Buscas que terminaram SEM trazer o mes. Empresa sem dado no periodo continua "faltando" depois da
 * busca — sem lembrar que ja buscou, o card redesenharia e buscaria de novo para sempre.
 */
const buscasFeitas = new Set();

class IndicatorValuesComponent {
    constructor($mdMedia, $stateParams, $filter, $scope) {
        this.$mdMedia = $mdMedia;
        this.$stateParams = $stateParams;
        this.$filter = $filter;
        this.PointDomain = new Point();
        this.PointCalculatedDomain = new PointCalculated();
        this.SerieDomain = new Serie();
        this.CalculatingMachine = new CalculatingMachine({
            Serie: this.SerieDomain,
            Point: this.PointDomain,
            PointCalculated: this.PointCalculatedDomain
        });
        this.FormatUnits = new FormatUnits($filter);
        this.Available = new Available();
        this.CalculatingMachineV2Gateway = new CalculatingMachineV2Gateway();
        this.$scope = $scope;

        this.companyId = this.$stateParams.id;
        this.isHighlight = false;
        this._currentPeriod = {};
        this._currentComparation = {};

        // A preferencia "Acumulado / Mensal" muda no popover da Base de dados sem trocar nenhum
        // binding deste card.
        $scope.$on("DATA_VALUES_MODE_CHANGED", () => this._setData());
    }

    $onInit() {
        this._setData();
    }

    $onChanges(changes) {
        if (
            !_.isEmpty(_.get(changes, "currentComparation.previousValue")) ||
            !_.isEmpty(_.get(changes, "currentPeriod.previousValue"))
        ) {
            this._setData();
        }

        if (this.fakeData && _.get(changes, "seriesData.currentValue.length"))
            this._setData();

        if (
            !this.fakeData &&
            _.get(changes, "seriesData.currentValue.length") &&
            _.get(changes, "seriesData.previousValue.length")
        )
            this._setData();
    }

    async _setData() {
        this._currentPeriod = angular.copy(this.currentPeriod);
        this._currentComparation = angular.copy(this.currentComparation);
        const points = this._getPoints();
        if (points) {
            this.seriesComparations = points.map((point) => {
                return {
                    ...point,
                    current: this._formatValuePoint(point.unit, point.current),
                    // "Jan–Ago 2025 (R$ 19.160.900,03)": o outro lado vai no tooltip da variacao.
                    yearToDate: point.yearToDate && {
                        ...point.yearToDate,
                        comparationTooltip: `${
                            point.yearToDate.comparationLabel
                        } (${this._formatValuePoint(point.unit, point.prev)})`
                    }
                };
            });
            const series = _.get(this.visualization, "series") || [];
            this.seriesComparations = this.seriesComparations.map((serie) => {
                return {
                    ...serie,
                    i: series.findIndex((v) => v == serie._id)
                };
            });
        }
    }

    _formatValuePoint(unit = "currency", value = 0) {
        let fixedValue = 0;

        if (["percentage", "number"].includes(unit)) fixedValue = 2;

        return this.FormatUnits.formatValue(unit, value, fixedValue);
    }

    _getComparationPoints(seriesComparations) {
        this.CalculatingMachine.setSource(this.visualization.sources[0]);
        const xFilter = this._currentPeriod;
        const yFilter = this._currentComparation;
        if (this.budget) {
            xFilter.scenario._id = this.budget;
            yFilter.scenario._id = this.budget;
        }
        const comparar = (x, y, accumulated = this.isAccumulated) =>
            this.CalculatingMachine.comparation({
                series: seriesComparations,
                companyId: this.companyId,
                xFilter: x,
                yFilter: y,
                fakeData: this.fakeData,
                accumulated,
                isGroupCompanies: _.get(this.companyData, "isGroup"),
                options: {
                    groupFilterOper: this.currentFilterGroup,
                    totalGroupCompanies: _.get(
                        this.companyData,
                        "companyIds.length",
                        0
                    )
                }
            });

        // Trimestre, semestre e ano ja sao janelas: o ponto deles ja vem somado.
        if (!this.budget && !this.fakeData && xFilter.period === "monthly") {
            const janela = accumulationWindow({
                mode: dataValuesMode(this.companyData?.preferences),
                baseDate: xFilter.date,
                startDate: xFilter.startDate,
                comparation: {
                    active: yFilter.active,
                    allMonths: yFilter.allMonths,
                    date: yFilter.date
                },
                lastMonthAvailable: yFilter.allMonths
                    ? this._lastMonthAvailable(xFilter, yFilter)
                    : null
            });
            if (janela)
                return this._getYearToDatePoints(
                    seriesComparations,
                    xFilter,
                    yFilter,
                    janela,
                    comparar
                );
        }
        return comparar(xFilter, yFilter);
    }

    /**
     * Na comparacao "todos os meses" o card soma de janeiro nos dois anos, e o dashboard pode nao ter
     * carregado o comeco de algum deles. Quando falta o primeiro mes de algum lado, calcula os anos
     * inteiros que ele cobre, so das series do card, e redesenha.
     *
     * Devolve `true` enquanto busca — o card esconde o comparado ate la, em vez de mostrar uma soma
     * parcial.
     */
    _loadMissingMonths(seriesComparations, lados, sumMonths) {
        if (!sumMonths || !this.companyData?._id) return false;
        const source = this.visualization.sources[0];
        const seriesIds = seriesComparations.map((serie) => serie._id);
        const falta = ({ start, scenarioId }) =>
            seriesIds.some(
                (id) =>
                    !this.CalculatingMachineV2Gateway.getCalculatedSeriesPoints(
                        [id],
                        start,
                        scenarioId,
                        "monthly"
                    ).length
            );
        const faltando = lados.filter(falta);
        const anosDe = ({ start, end }) => {
            const anos = [];
            for (let a = start.getUTCFullYear(); a <= end.getUTCFullYear(); a++)
                anos.push(a);
            return anos;
        };
        const chaveDe = (lado) =>
            [
                this.companyData._id,
                source,
                lado.scenarioId,
                anosDe(lado).join(","),
                [...seriesIds].sort().join(",")
            ].join("|");
        const aBuscar = faltando.filter((lado) => !buscasFeitas.has(chaveDe(lado)));
        if (!aBuscar.length) return false;

        const buscas = aBuscar.map((lado) => {
            const chave = chaveDe(lado);
            if (!buscasDoAcumulado.has(chave))
                buscasDoAcumulado.set(
                    chave,
                    this.CalculatingMachineV2Gateway.calculateSeries({
                        companyData: this.companyData,
                        source,
                        seriesIds,
                        scenarioId: lado.scenarioId,
                        analyzeGroup: _.get(this.currentFilterGroup, "value"),
                        analysisPeriod: "monthly",
                        allMonthsByYear: anosDe(lado)
                    }).finally(() => {
                        buscasDoAcumulado.delete(chave);
                        // So marca quando a busca VEIO VAZIA (empresa sem dado no periodo). Se veio
                        // dado e o mapa for trocado depois (`preserveMap: false` em outra tela), a
                        // proxima passada busca de novo em vez de somar zeros.
                        if (falta(lado)) buscasFeitas.add(chave);
                    })
                );
            return buscasDoAcumulado.get(chave);
        });
        Promise.all(buscas).then(() =>
            this.$scope.$applyAsync(() => this._setData())
        );
        return true;
    }

    /**
     * O ultimo mes com dado nos DOIS anos: o menor dos dois. Comparar 2025 inteiro com 2026 ate
     * agosto mediria calendario, nao desempenho.
     */
    _lastMonthAvailable(xFilter, yFilter) {
        const ultimoDe = (filtro) => {
            if (!filtro?.date) return null;
            const { availableDates } = this.Available.get({
                companyId: this.companyId,
                scenarioId: filtro.scenario._id,
                source: this.visualization.sources[0],
                options: { separatted: true }
            });
            return lastMonthWithData(
                availableDates,
                filtro.date.getUTCFullYear()
            );
        };
        const meses = [ultimoDe(xFilter), ultimoDe(yFilter)].filter(Boolean);
        return meses.length ? Math.min(...meses) : null;
    }

    /**
     * O card por JANELA — o periodo das barras somado, ou o ultimo mes na comparacao "todos os
     * meses" do modo mensal. Ver `accumulationWindow` e `mergeMonthlyComparations`.
     */
    _getYearToDatePoints(seriesComparations, xFilter, yFilter, janela, comparar) {
        const { sumMonths } = janela;
        const mesesBase = monthsBetween(janela.base.start, janela.base.end);
        const mesesComparados = monthsBetween(
            janela.comparation.start,
            janela.comparation.end
        );
        // Sem variacao (acumulado sem comparacao) o outro lado nem e mostrado: nao vale buscar.
        const carregando = this._loadMissingMonths(
            seriesComparations,
            [
                { ...janela.base, scenarioId: xFilter.scenario._id },
                ...(janela.showVariation
                    ? [{ ...janela.comparation, scenarioId: yFilter.scenario._id }]
                    : [])
            ],
            sumMonths
        );
        // Sempre o valor do MES (`accumulated` indefinido, a leitura padrao do dashboard), mesmo com
        // a visualizacao em modo acumulado: a metrica `a` do calculatrix so e acumulado de verdade
        // em parte das series contabeis — no fiscal, pessoal e fin ela e o proprio mes.
        // `mensais[i]` e o i-esimo mes dos dois lados. Sem soma, so o ultimo interessa.
        const total = Math.min(mesesBase.length, mesesComparados.length);
        const mensais = [];
        for (let i = 0; i < total; i++) {
            const precisa = sumMonths || i === total - 1;
            mensais.push(
                precisa
                    ? comparar(
                          { ...xFilter, date: mesesBase[i] },
                          { ...yFilter, date: mesesComparados[i] },
                          undefined
                      )
                    : []
            );
        }

        // Quem NAO soma le o ultimo mes do jeito que o grafico le — com o toggle acumulado da
        // visualizacao. Quem soma precisa do valor do mes (a leitura de `mensais`).
        const ultimoComToggle = total
            ? comparar(
                  { ...xFilter, date: mesesBase[total - 1] },
                  { ...yFilter, date: mesesComparados[total - 1] }
              )
            : [];
        const doUltimoMes = mensais[total - 1] || [];
        return mergeMonthlyComparations(mensais, seriesComparations, {
            sumMonths
        }).map((somado) => {
            const { _id, sums } = somado;
            const comToggle = ultimoComToggle.find((item) => item._id === _id);
            const current = sums ? somado.current : comToggle?.current ?? 0;
            const prev = !janela.showVariation
                ? current
                : sums
                ? somado.prev
                : comToggle?.prev ?? 0;
            const base = doUltimoMes.find((item) => item._id === _id) || {};
            const value = current - prev;
            const variationCondition =
                this.CalculatingMachine.variationCondition(
                    current,
                    prev,
                    base.polarity
                );
            return {
                ...base,
                current,
                prev,
                value,
                percentage: !prev ? 0 : value / prev,
                progress: this.CalculatingMachine.variationProgressBar(
                    current,
                    prev
                ),
                variationCondition,
                polarityColor: this.CalculatingMachine.polarityColor(
                    { polarity: base.polarity, current, prev, value },
                    true,
                    variationCondition
                ),
                yearToDate: {
                    loading: carregando,
                    showVariation: janela.showVariation,
                    periodLabel: valueTooltip(janela.base, sums),
                    comparationLabel: rangeLabel(janela.comparation, sums)
                }
            };
        });
    }

    async _getFakePoints() {
        const seriesComparations = await this._getSeriesComparationsFromCloud(
            this.visualization,
            this.$mdMedia("xs")
        );

        if (seriesComparations.length)
            return this._getComparationPoints(seriesComparations);
    }

    _getPoints() {
        const seriesComparations = this._getSeriesComparations(
            this.visualization,
            this.$mdMedia("xs")
        );

        if (seriesComparations.length)
            return this._getComparationPoints(seriesComparations);
        else return [];
    }

    _limit(width, isXs, min = 1) {
        if (isXs) return 2;
        if (width == 100) return 5;
        return width <= 33 ? min : 3;
    }

    _getSerieId(visualization, isXs) {
        if (visualization.highlightsSeriesIds) {
            this._setHighlight(true);
            return visualization.highlightsSeriesIds;
        }

        if (!Array.isArray(visualization.series)) return [];

        // if (visualization.mainSerieId) {
        //     return visualization.mainSerieId;
        // }

        if (visualization.series.length > 1) {
            if (visualization.series.length <= 3) {
                return _.slice(visualization.series, 0, this._limit(100, isXs));
            } else if (visualization.series.length <= 5) {
                return _.slice(
                    visualization.series,
                    0,
                    this._limit(this.columnWidth, isXs, 0)
                );
            }

            return [];
        }

        return visualization.series;
    }

    _setHighlight(isHighlight) {
        this.isHighlight = isHighlight;
    }

    /**
     * @private
     * Set the series to show in comparation
     * @param {Object} customSeries
     * @param {String} highlightsSeriesIds
     * @param {Array} allSeries
     * @param {String} mainSerieId
     * @returns {Array}
     */
    _getSeriesComparations(visualization, isXs) {
        let seriesToComapartion = [].concat(
            this._getSerieId(visualization, isXs)
        );

        return this.SerieDomain.findCollection(
            { _id: { $in: seriesToComapartion } },
            {},
            "array"
        );
    }

    /**
     * @private
     * Set the series to show in comparation
     * @param {Object} customSeries
     * @param {String} highlightsSeriesIds
     * @param {Array} allSeries
     * @param {String} mainSerieId
     * @returns {Array}
     */
    async _getSeriesComparationsFromCloud(visualization, isXs) {
        let seriesToComapartion = [].concat(
            this._getSerieId(visualization, isXs)
        );

        return await this.SerieDomain.getByIds({
            seriesIds: seriesToComapartion,
            companyIds: [this.companyId]
        });
    }
}

export { IndicatorValuesComponent };

IndicatorValuesComponent.$inject = [
    "$mdMedia",
    "$stateParams",
    "$filter",
    "$scope"
];

const name = "indicatorValuesComponent";
const def = {
    bindings: {
        visualization: "<",
        currentPeriod: "<",
        currentFilterGroup: "<",
        currentComparation: "<",
        source: "<",
        fakeData: "<",
        seriesData: "<",
        isAccumulated: "<",
        companyData: "<",
        yearlyPoints: "<",
        budget: "<",
        columnWidth: "<"
    },
    controller: IndicatorValuesComponent,
    template
};
angular.module("nucont.shared").component(name, def);
export default { name, ...def };
